/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { Fragment, useEffect, useRef, useState } from 'react'
import { Input, Radio } from 'antd'
import { observer } from 'mobx-react'
import _ from 'lodash'

import {
  isNotStartedMatchStatus,
  maxImageFileSizeValidate,
  showToast,
  TOAST_TYPE
} from '@utils/utils'
import useRootStore from '@store/useRootStore'
// css
import { Button } from '@styles/button.style'
import { EmoticonIco, ImageUploadIco } from '@shared/Icon/Chat'
import { BettingHistoryIco } from '@shared/Icon/MenuIcon'
import { isMobile } from 'react-device-detect'
import { ATTACHMENT_TYPE, FROM_VIEW_TYPE, GlobalStoreKey, USER_PENALTY_TYPE } from '@v2/constants/common'
import SimpleBettingHistory from '@components/SimpleBettingHistory'
import { BettingInfo, BettingRecordTrack } from '@store/bettingHistoryStore'
import { BeforeWriteWrap, CheerForTeamSelectAreaWrap, TextAreaWrap } from './index.style'
import { EmoticonArea } from '@styles/base.style'
import BottomLoading from '@components/BottomLoading'
import classNames from 'classnames'
import DialogUtils from '@utils/dialog-utils'
import { SimpleCheerForTeamMatchInfo } from '@components/SimpleCheerForTeamMatchInfo'
import { CheerForTeamEmoticonListStyle } from '@views/Popup/Chat/index.style'
import * as ImageUtils from '@utils/image-utils'
import { DEFAULT_EFFECT_ICON_ID } from '@utils/image-utils'
import Login from '@views/Login'
import { Schema$CheerForTeamData } from '@protocol/chat'
import { CHEER_FOR_TEAM_CONTENTS_TYPE } from '@v2/constants/cheer'
import { RadioChangeEvent } from 'antd/lib/radio'
import { ChatSettingLabel } from '@views/Popup/Chat/ChatSettingPopup/index.style'
import AdvImg from '@components/AdvImg'

const { TextArea } = Input

export interface AdvEditorData {
  isNew: boolean
  content: string
  filteredContent?: string
  attachmentType: ATTACHMENT_TYPE | CHEER_FOR_TEAM_CONTENTS_TYPE
  attachment: string
  attachmentDetail: string
  cheerForTeamData?: Schema$CheerForTeamData
}

interface AdvEditorProps {
  className?: string
  editorData?: AdvEditorData
  isFocus: boolean
  beforePlaceholder?: string
  placeholder: string
  saveCommentTxt?: string
  fromViewType: FROM_VIEW_TYPE
  showBettingHistory?: boolean
  callbackClose?: () => void
  callbackOpen?: () => void
  callbackSave?: (pickEditorData: AdvEditorData) => Promise<boolean>
  showBettingShare?: boolean
  minLength?: number
  maxLength?: number
  maxLine?: number
  showCheerForTeam?: boolean
}

/**
 * 프리미엄 분석가 댓글 또는 나도 승부사 편집시 나오는 편집 컴포넌트.
 * @param props
 * @constructor
 */
export const AdvEditor: React.FC<AdvEditorProps> = observer((props) => {
  const {
    className,
    editorData = {
      isNwe: true,
      content: '',
      attachment: null,
      attachmentType: ATTACHMENT_TYPE.TEXT_ONLY,
      attachmentDetail: null,
      cheerForTeamData: null,
    } as unknown as AdvEditorData,
    isFocus,
    beforePlaceholder,
    placeholder,
    saveCommentTxt,
    fromViewType,
    showBettingHistory,
    callbackClose,
    callbackOpen,
    callbackSave,
    showBettingShare = true,
    minLength = 0,
    maxLength = 3000,
    maxLine = 300,
    showCheerForTeam,
  } = props

  const { globalStore, chattingStore, authStore, bettingHistoryStore, penaltyStore, cheerForTeamStore } = useRootStore()
  const { userInfo } = authStore
  const { emoticonList } = chattingStore
  const { selectedBettingHistory, selectedBettingHistoryFoldData, selectedBettingHistoryFold } = bettingHistoryStore
  const [isCheerForTeamInit, setIsCheerForTeamInit] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!isCheerForTeamInit && !_.isNil(editorData.cheerForTeamData?.matchId)) {
      setIsCheerForTeamInit(true)
      return
    } else {
      if (isCheerForTeamInit && _.isNil(editorData.cheerForTeamData?.matchId)) {
        setIsCheerForTeamInit(false)
      }
      contentRef.current.blur()
      resetContent()
      setVisibleBeforeWriteBox(true)
    }
  }, [editorData.cheerForTeamData?.matchId])

  const emoticonUrlPrefix = globalStore.findGlobalSetting(GlobalStoreKey.CHAT_EMOTICON_URL_PREFIX)?.value

  let attachImgUrlPrefix: string
  if (fromViewType === FROM_VIEW_TYPE.MATCHUP_DETAIL_CHEER_FOR_TEAM
    || fromViewType === FROM_VIEW_TYPE.SPORT_TALK) {
    attachImgUrlPrefix = globalStore.findGlobalSetting(
      GlobalStoreKey.CHAT_ATTACH_IMAGE_URL_PREFIX,
    )?.value
  } else {
    attachImgUrlPrefix = globalStore.findGlobalSetting(
      GlobalStoreKey.USER_COMMENT_ATTACH_IMAGE_URL_PREFIX,
    )?.value
  }

  let commentBtnClass = 'send-btn'

  let commentContent = ''
  if (!editorData.isNew) {
    if (editorData.filteredContent) {
      commentContent = editorData.filteredContent
    } else {
      commentContent = editorData.content
    }
  }

  let previewImageUrl
  let previewImageName
  let previewEmoticonUrl
  let previewEmoticonName
  let previewBettingHistory
  let pickCommentTextareaClassName

  if (fromViewType === FROM_VIEW_TYPE.COMPANY_PICK_COMMENT || fromViewType === FROM_VIEW_TYPE.USER_PICK_COMMENT
    || fromViewType === FROM_VIEW_TYPE.MATCHUP_DETAIL_CHEER_FOR_TEAM || fromViewType === FROM_VIEW_TYPE.SPORT_TALK) {
    pickCommentTextareaClassName = 'pick-comment-textarea'
  } else if (fromViewType === FROM_VIEW_TYPE.USER_PICK) {
    pickCommentTextareaClassName = 'pick-comment-textarea user-pick'
  }

  if (!editorData.isNew) {
    switch (editorData.attachmentType) {
      case ATTACHMENT_TYPE.IMAGE:
        previewImageName = editorData.attachment
        previewImageUrl = `${attachImgUrlPrefix}${editorData.attachment}`
        break
      case ATTACHMENT_TYPE.EMOTICON:
        previewEmoticonName = editorData.attachment
        previewEmoticonUrl = `${emoticonUrlPrefix}${editorData.attachment}`
        break
      case ATTACHMENT_TYPE.BET_HISTORY:
        previewBettingHistory = {
          betId: editorData.attachment,
          bet: JSON.parse(editorData.attachmentDetail) as BettingInfo,
        } as BettingRecordTrack
        break
    }

    pickCommentTextareaClassName += ' edit'
  }

  const effectIconList = ImageUtils.getCheerForTeamEffectIconList()
  const defaultEffectIcon = _.find(effectIconList, icon => icon.id === DEFAULT_EFFECT_ICON_ID)

  const [visibleBeforeWriteBox, setVisibleBeforeWriteBox] = useState<boolean>(true)
  const [emoticonAreaClass, setEmoticonAreaClass] = useState<string>('comment-emoticon')
  const [content, setContent] = useState<string>(commentContent)
  const [previewBetHistory, setPreviewBetHistory] = useState(previewBettingHistory)
  const [effectIcon, setEffectIcon] = useState(defaultEffectIcon)

  if(!_.isNil(editorData?.cheerForTeamData)) {
    editorData.cheerForTeamData = { ...editorData.cheerForTeamData, effectIconUrl: effectIcon.img }
  }

  const [imageState, setImageState] = useState<{
    imageFile: File, previewUrl: string, uploadedImageName: string,
  }>({
    imageFile: null,
    previewUrl: previewImageUrl,
    uploadedImageName: previewImageName,
  })

  const [emoticonState, setEmoticonState] = useState<{
    imageName: string, previewUrl: string,
  }>({
    imageName: previewEmoticonName,
    previewUrl: previewEmoticonUrl,
  })
  const [isImgUploading, setImgUploading] = useState(false)

  const contentRef = useRef(null)
  const imageRef = useRef(null)

  useEffect(() => {
    if (selectedBettingHistory[fromViewType] && showBettingShare) {
      hideEmoticonsPanel()
      setEmoticonState({ imageName: null, previewUrl: null })
      setImageState({ imageFile: null, previewUrl: null, uploadedImageName: null })
      const clonedSelectedBettingHistory = _.cloneDeep(selectedBettingHistory[fromViewType])
      if (selectedBettingHistoryFoldData[fromViewType]) {
        clonedSelectedBettingHistory.bet['displayFolds'] = []
        clonedSelectedBettingHistory.bet['displayFolds'].push({
          foldId: selectedBettingHistoryFoldData[fromViewType]?.foldId,
          selectionName: selectedBettingHistoryFoldData[fromViewType]?.selectionName,
          outcomeRate: selectedBettingHistoryFoldData[fromViewType]?.outcomeRate,
          outcomeName: selectedBettingHistoryFoldData[fromViewType]?.marketName,
          specialBetValue: selectedBettingHistoryFoldData[fromViewType]?.specialBetValue,
          hitState: selectedBettingHistoryFoldData[fromViewType]?.hitState,
          homeTeamName: selectedBettingHistoryFold[fromViewType]?.homeTeamName,
          awayTeamName: selectedBettingHistoryFold[fromViewType]?.awayTeamName,
          sportId: selectedBettingHistoryFold[fromViewType]?.sportId,
        })
      }
      setPreviewBetHistory(clonedSelectedBettingHistory)
    } else if (previewBettingHistory && !selectedBettingHistory[fromViewType]) {
      setPreviewBetHistory(previewBettingHistory)
    } else if (previewImageName && !selectedBettingHistory[fromViewType]) {
      setImageState({
        uploadedImageName: previewImageName,
        previewUrl: previewImageUrl,
        imageFile: imageState.imageFile,
      })
      setPreviewBetHistory(null)
    } else if (previewEmoticonName && !selectedBettingHistory[fromViewType]) {
      setEmoticonState({
        imageName: previewEmoticonName,
        previewUrl: previewEmoticonUrl,
      })
      setPreviewBetHistory(null)
    } else if (!previewBettingHistory && !selectedBettingHistory[fromViewType]) {
      setPreviewBetHistory(null)
    }
  }, [selectedBettingHistory[fromViewType]])

  useEffect(() => {
    if (emoticonState.imageName) {
      setImageState({ imageFile: null, previewUrl: null, uploadedImageName: null })
      setPreviewBetHistory(null)
      if (showBettingShare) {
        bettingHistoryStore.setSelectedBettingHistoryByBettingRecordTrack(null, fromViewType)
      }
    }
  }, [emoticonState])

  useEffect(() => {
    if (imageState.previewUrl) {
      hideEmoticonsPanel()
      setEmoticonState({ imageName: null, previewUrl: null })
      setPreviewBetHistory(null)
      if (showBettingShare) {
        bettingHistoryStore.setSelectedBettingHistoryByBettingRecordTrack(null, fromViewType)
      }
    }
  }, [imageState])

  useEffect(() => {
    if (isFocus) {
      contentRef.current.focus()
      setVisibleBeforeWriteBox(false)
    } else {
      contentRef.current.blur()
      resetContent()
      setVisibleBeforeWriteBox(true)
    }
  }, [isFocus])

  const getTextLength = (inputText?: string): number => {
    if (!inputText) {
      return 0
    }
    return _.chain(inputText).replace(/\n/gi, '').trim().size().value()
  }

  const onTextAreaChange = (inputText: string): void => {
    if (getTextLength(inputText) > maxLength) {
      showToast(TOAST_TYPE.WARN, `${maxLength}자를 초과하였습니다.`)
      setContent(content)
      return
    }

    if (_.split(inputText, '\n').length > maxLine) {
      showToast(TOAST_TYPE.WARN, `${maxLine}줄을 초과하였습니다.`)
      setContent(content)
      return
    }

    setContent(inputText)
  }

  const updateEditorData = async (): Promise<boolean> => {
    let attachmentType: ATTACHMENT_TYPE
    let attachment: string
    let attachmentDetail: string

    if (!_.isNil(imageState.uploadedImageName)) {
      attachmentType = ATTACHMENT_TYPE.IMAGE
      attachment = imageState.uploadedImageName
      attachmentDetail = null
    } else if (emoticonState.imageName) {
      attachmentType = ATTACHMENT_TYPE.EMOTICON
      attachment = emoticonState.imageName
      attachmentDetail = null
    } else if (showBettingHistory && previewBetHistory) {
      attachmentType = ATTACHMENT_TYPE.BET_HISTORY
      attachment = previewBetHistory.betId // bettor_bet id
      const sportIdSet = new Set<number>()
      _.forEach(previewBetHistory?.folds, fold => {
        sportIdSet.add(fold.sportId)
      })
      attachmentDetail = JSON.stringify({
        betId: previewBetHistory?.bet.id, // bettor_bet betId (클럽벳 조회용 - 어드민에서사용)
        foldCount: previewBetHistory?.bet.foldCount,
        dividendRate: previewBetHistory?.bet.dividendRate,
        hitState: previewBetHistory?.bet.hitState,
        sportIds: Array.from<number>(sportIdSet),
        cancel: previewBetHistory?.bet.cancel
      })
    } else {
      attachmentType = ATTACHMENT_TYPE.TEXT_ONLY
      attachment = null
      attachmentDetail = null
    }

    if (!validationTextContent(attachmentType)) {
      return false
    }

    editorData.content = _.trim(content)
    editorData.attachmentType = attachmentType
    editorData.attachment = attachment
    editorData.attachmentDetail = attachmentDetail

    if (!_.isNil(editorData?.cheerForTeamData)) {
      editorData.cheerForTeamData.effectIconUrl = effectIcon.img
    }

    let result
    if (callbackSave) {
      result = await callbackSave(editorData)
    }
    if (callbackClose) {
      callbackClose()
    }
    return result
  }

  const validationTextContent = (type: ATTACHMENT_TYPE): boolean => {
    if (type === ATTACHMENT_TYPE.TEXT_ONLY && getTextLength(content) <= 0) {
      showToast(TOAST_TYPE.WARN, '댓글 입력이 되지 않았습니다.')
      return false
    }
    return true
  }

  const onClickLoginHandler = () => {
    authStore.showLoginPopup(<Login/>)
  }

  const onClickOpenTextarea = (): void => {
    if (!authStore.isSignedIn) {
      onClickLoginHandler()
    } else {
      globalStore.clearActivityScoreNoticeQueue()
      contentRef.current.focus()
      setVisibleBeforeWriteBox(false)
    }
    if (callbackOpen) {
      callbackOpen()
    }
  }

  const onClickCloseTextarea = (): void => {
    contentRef.current.blur()
    resetContent()
    setVisibleBeforeWriteBox(true)
    if (callbackClose) {
      callbackClose()
    }
  }

  const onClickEmoticonHandler = (emoticonUrl): void => {
    setEmoticonState({
      imageName: emoticonUrl,
      previewUrl: `${emoticonUrlPrefix}${emoticonUrl}`,
    })
  }

  const hideEmoticonsPanel = (): void => {
    setEmoticonAreaClass('comment-emoticon')
  }

  const onClickAttachEmoticon = (): void => {
    if (emoticonAreaClass === 'comment-emoticon') {
      contentRef.current.blur()
      setEmoticonAreaClass('comment-emoticon show')
    } else {
      hideEmoticonsPanel()
    }
  }

  const onClickBettingListShareButton = (): void => {
    contentRef.current.blur()
    hideEmoticonsPanel()
    bettingHistoryStore.resetSelectedBettingHistory(fromViewType)
    bettingHistoryStore.setBettingHistoryInitProps({ onlyMajorClub: true, fromViewType })
    globalStore.setVisibleBettingListPopup(true, true, true)
  }

  const onClickClosePreview = (): void => {
    resetPreviewAttachment()
    resetImageFileInput()
  }

  const onTouchStartImageAttachHandler = (): void => {
    contentRef.current.blur()
    hideEmoticonsPanel()
    globalStore.setIgnoreVisibleChangeHandler(true)
  }

  const handleFileOnChange = (event): void => {
    event.preventDefault()
    const reader = new FileReader()
    const file = event.target.files[0]
    if (maxImageFileSizeValidate(file)) { // TODO 첨부 이미지 최대 용량을 글로벌로 관리 해도 괜찮을까?
      reader.onload = () => {
        setImgUploading(true)

        const onImageUploaded = (uploadedImageName: string): void => {
          setImgUploading(false)
          setImageState({
            imageFile: file,
            previewUrl: `${attachImgUrlPrefix}${uploadedImageName}`,
            uploadedImageName,
          })
        }

        if (fromViewType === FROM_VIEW_TYPE.MATCHUP_DETAIL_CHEER_FOR_TEAM
          || fromViewType === FROM_VIEW_TYPE.SPORT_TALK) {
          globalStore.uploadChatImageToAwsS3(file).then(onImageUploaded).catch(console.error)
        } else {
          globalStore.uploadCommentImageToAwsS3(file).then(onImageUploaded).catch(console.error)
        }
      }
      reader.readAsDataURL(file)
    }
    globalStore.setIgnoreVisibleChangeHandler(false)
    resetImageFileInput()
  }

  const onClickOpenUserBettingListInfo = (betId: string, nick: string): void => {
    DialogUtils.showSharedBettingList({ betId, fromUserNick: nick })
  }

  const saveEditorData = async (): Promise<boolean> => {
    if (!editorData.isNew) {
      return updateEditorData()
    }

    let attachmentType: any = ATTACHMENT_TYPE.TEXT_ONLY
    let attachment: string
    let attachmentDetailObj: any
    if (!_.isNil(imageState.uploadedImageName)) {
      attachmentType = ATTACHMENT_TYPE.IMAGE
      attachment = imageState.uploadedImageName
    } else if (emoticonState.imageName) {
      attachmentType = ATTACHMENT_TYPE.EMOTICON
      attachment = emoticonState.imageName
    } else if (selectedBettingHistory[fromViewType] && showBettingShare) {
      const sportIdSet = new Set<number>()
      _.forEach(selectedBettingHistory[fromViewType]?.folds, fold => {
        sportIdSet.add(fold.sportId)
      })
      attachmentType = ATTACHMENT_TYPE.BET_HISTORY
      attachment = selectedBettingHistory[fromViewType].betId // bettor_bet id
      attachmentDetailObj = {
        betId: selectedBettingHistory[fromViewType]?.bet.id, // bettor_bet betId (클럽벳 조회용 - 어드민에서사용)
        foldCount: selectedBettingHistory[fromViewType]?.bet.foldCount,
        dividendRate: selectedBettingHistory[fromViewType]?.bet.dividendRate,
        hitState: selectedBettingHistory[fromViewType]?.bet.hitState,
        sportIds: Array.from<number>(sportIdSet),
        canceled: selectedBettingHistory[fromViewType]?.bet.canceled,
      }

      if (selectedBettingHistoryFoldData[fromViewType]) {
        attachmentDetailObj['displayFolds'] = []
        attachmentDetailObj['displayFolds'].push({
          foldId: selectedBettingHistoryFoldData[fromViewType]?.foldId,
          selectionName: selectedBettingHistoryFoldData[fromViewType]?.selectionName,
          outcomeRate: selectedBettingHistoryFoldData[fromViewType]?.outcomeRate,
          outcomeName: selectedBettingHistoryFoldData[fromViewType]?.marketName,
          specialBetValue: selectedBettingHistoryFoldData[fromViewType]?.specialBetValue,
          hitState: selectedBettingHistoryFoldData[fromViewType]?.hitState,
          homeTeamName: selectedBettingHistoryFold[fromViewType]?.homeTeamName,
          awayTeamName: selectedBettingHistoryFold[fromViewType]?.awayTeamName,
          sportId: selectedBettingHistoryFold[fromViewType]?.sportId,
        })
      }

      // 에디터에서 응원글 등록도 다루기 때문에 응원글 작성인 경우 attachmentType 을 응원글용 첨부 타입으로 변환 해준다.
      if (fromViewType === FROM_VIEW_TYPE.MATCHUP_DETAIL_CHEER_FOR_TEAM
      || fromViewType === FROM_VIEW_TYPE.SPORT_TALK) {
        switch (attachmentType) {
          case ATTACHMENT_TYPE.TEXT_ONLY:
            attachmentType = CHEER_FOR_TEAM_CONTENTS_TYPE.TEXT_ONLY
            break
          case ATTACHMENT_TYPE.IMAGE:
            attachmentType = CHEER_FOR_TEAM_CONTENTS_TYPE.IMAGE
            break
          case ATTACHMENT_TYPE.EMOTICON:
            attachmentType = CHEER_FOR_TEAM_CONTENTS_TYPE.EMOTICON
            break
          case ATTACHMENT_TYPE.BET_HISTORY:
            attachmentType = CHEER_FOR_TEAM_CONTENTS_TYPE.BET_HISTORY
            break
        }
      }
    }

    const newEditorData: AdvEditorData = {
      isNew: true,
      content: _.trim(content),
      attachmentType,
      attachment,
      attachmentDetail: JSON.stringify(attachmentDetailObj),
      cheerForTeamData: editorData.cheerForTeamData,
    }

    if (!validationTextContent(attachmentType)) {
      return false
    }
    let isSuccess: boolean
    if (callbackSave) {
      isSuccess = await callbackSave(newEditorData)
    }
    if (isSuccess) {
      resetContent()
    }
    return isSuccess
  }

  const onClickSaveData = async (): Promise<void> => {
    if (!commentBtnClass.includes('active')) {
      return
    }

    if (isSaving) {
      return
    }

    let penaltyType

    if (fromViewType === FROM_VIEW_TYPE.COMPANY_PICK_COMMENT) {
      penaltyType = USER_PENALTY_TYPE.COMPANY_PICK
    } else if (fromViewType === FROM_VIEW_TYPE.USER_PICK
      ||fromViewType === FROM_VIEW_TYPE.USER_PICK_COMMENT) {
      penaltyType = USER_PENALTY_TYPE.USER_PICK
    } else if (fromViewType === FROM_VIEW_TYPE.MATCHUP_DETAIL_CHEER_FOR_TEAM
    || fromViewType === FROM_VIEW_TYPE.SPORT_TALK) {
      penaltyType = USER_PENALTY_TYPE.CHEER_FOR_TEAM
    }

    setIsSaving(true)

    const penaltyMsg = await penaltyStore.checkUserPenalty(penaltyType)
    if (_.isEmpty(penaltyMsg)) {
      const result = await saveEditorData()
      if (result) {
        setVisibleBeforeWriteBox(true)
        resetPreviewAttachment()
      }
    } else {
      DialogUtils.showPenaltyInfo(penaltyMsg)
    }

    setIsSaving(false)
  }

  const resetContent = (): void => {
    if (contentRef.current && contentRef.current.state) {
      contentRef.current.state.value = ''
    }
    resetImageFileInput()
    setContent('')
    setEffectIcon(defaultEffectIcon)
    hideEmoticonsPanel()
    resetPreviewAttachment()
  }

  const resetImageFileInput = (): void => {
    if (imageRef.current) {
      imageRef.current.value = ''
    }
  }

  const resetPreviewAttachment = (): void => {
    setImageState({ imageFile: null, previewUrl: null, uploadedImageName: null })
    setEmoticonState({ imageName: null, previewUrl: null })
    setPreviewBetHistory(null)
    if (showBettingShare) {
      bettingHistoryStore.setSelectedBettingHistoryByBettingRecordTrack(null, fromViewType)
    }
  }

  let minRowsValue = 5
  let maxRowsValue = 7.5
  if (!editorData.isNew) {
    if (isMobile) {
      minRowsValue = 5
      maxRowsValue = 9
    } else {
      minRowsValue = 9
      maxRowsValue = 13
    }
  }

  const updateCommentBtnClassName = (type: ATTACHMENT_TYPE): void => {
    if (editorData.content !== content) {
      commentBtnClass = 'send-btn active'
    } else {
      commentBtnClass = 'send-btn'
    }

    switch (type) {
      case ATTACHMENT_TYPE.IMAGE:
        if (editorData.attachment !== imageState.uploadedImageName) {
          commentBtnClass = 'send-btn active'
        }
        break
      case ATTACHMENT_TYPE.EMOTICON:
        if (editorData.attachment !== emoticonState.imageName) {
          commentBtnClass = 'send-btn active'
        }
        break
      case ATTACHMENT_TYPE.BET_HISTORY:
        if (editorData.attachment !== previewBetHistory.betId && showBettingHistory) {
          commentBtnClass = 'send-btn active'
        }
        break
      case ATTACHMENT_TYPE.TEXT_ONLY:
        if (getTextLength(content) <= 0) {
          commentBtnClass = 'send-btn'
        } else {
          commentBtnClass = 'send-btn active'
        }
        if (editorData.attachment) {
          commentBtnClass = 'send-btn active'
        }
        break
    }

    if (!_.isNil(minLength) && getTextLength(content) < minLength) {
      commentBtnClass = 'send-btn'
    }
  }

  const simpleBettingHistoryClass = classNames('detail-bet-betting-list', {
    ['pc']: !isMobile,
  })

  const uploadLoading = <BottomLoading/>

  let previewContents: React.ReactElement
  let previewWrapClassName = 'upload-preview textarea'
  if (!_.isNil(imageState.imageFile) || !_.isEmpty(imageState.previewUrl)) {
    previewContents = (
      <Button TP className="attachment-img-btn">
        <AdvImg src={imageState.previewUrl} srcMode={'small'} alt="이미지 미리보기" className="upload-img-preview lhn2"/>
      </Button>
    )
    updateCommentBtnClassName(ATTACHMENT_TYPE.IMAGE)
  } else if (emoticonState.previewUrl) {
    previewContents = (
      <Button TP className="attachment-img-btn">
        <img className="upload-img-preview emoticon" src={emoticonState.previewUrl} alt="프리미엄 분석가 댓글 이모티콘 미리보기"/>
      </Button>
    )
    updateCommentBtnClassName(ATTACHMENT_TYPE.EMOTICON)
  } else if (previewBetHistory && showBettingHistory) {
    previewContents = (
      <SimpleBettingHistory
        bettingRecordTrack={previewBetHistory}
        onClickOpenBettingListInfo={() => {
          onClickOpenUserBettingListInfo(previewBetHistory.betId, userInfo.nick)
        }}
        className={simpleBettingHistoryClass}
      />
    )
    updateCommentBtnClassName(ATTACHMENT_TYPE.BET_HISTORY)
  } else {
    if (!isImgUploading) {
      previewWrapClassName += ' adv-hidden'
    }
    previewContents = undefined

    updateCommentBtnClassName(ATTACHMENT_TYPE.TEXT_ONLY)
  }

  const previewWrap = (
    <div className={previewWrapClassName}>
      {isImgUploading ? uploadLoading : previewContents}

      {/* 첨부 삭제 버튼 */}
      {/* TODO 업로드중에는 닫기 버튼이 보이지 않아야 합니다. */}
      {!isImgUploading &&
        <Button TP className="delete-preview-btn" onClick={onClickClosePreview}>
          <span className="bg"/>
          <span className="x-ico"/>
        </Button>
      }
    </div>
  )

  let saveCommentBtnTxt

  if (saveCommentTxt) {
    saveCommentBtnTxt = saveCommentTxt
  } else {
    if (editorData.isNew) {
      saveCommentBtnTxt = '게시'
    } else {
      saveCommentBtnTxt = '완료'
    }
  }

  let maxLengthClass
  if (getTextLength(content) < minLength || getTextLength(content) === maxLength) {
    maxLengthClass = 'max-length color-guide'
  } else {
    maxLengthClass = 'max-length'
  }

  const PickEditorClassName = classNames('write-comments-area', {
    'before': visibleBeforeWriteBox,
  })

  const onClickCheerForTeamEmoticonHandler = (event: RadioChangeEvent) => {
    const id = event.target.value
    // const url = .img
    // // setEffectIconUrl(url)
    // // setEffectIconId(id)
    setEffectIcon(effectIconList.find(item => item.id === id))
  }

  const { effectEnabled } = cheerForTeamStore

  const cheerForTeamClassName = classNames('', {
    'disabled': !effectEnabled,
  })

  {/* ************** 상세BET_응원글탭_응원글입력창 스타일을 위한 변수 ************** */}
  const isCheerForTeam = !visibleBeforeWriteBox && showCheerForTeam
    && !isNotStartedMatchStatus(editorData.cheerForTeamData?.matchStatus)

  return (
    <Fragment>
      {isCheerForTeam &&
        <CheerForTeamSelectAreaWrap className={cheerForTeamClassName}>
          <Radio.Group
            className="cheer-for-team-emoticon-list"
            onChange={effectEnabled ? onClickCheerForTeamEmoticonHandler : null}
            value={effectIcon.id}
            defaultValue={defaultEffectIcon.id}
            css={CheerForTeamEmoticonListStyle}
          >
            {effectIconList.map(item =>
              <Radio.Button
                key={`cheer-for-team_effect-icon-${item.id}`}
                value={effectEnabled ? item.id : null}
                className="cheer-for-team-emoticon-list--item"
              >
                <img className="cheer-for-team-emoticon-img"
                     src={effectEnabled ? item.pvImg : item.pvGrayImg}
                     alt={item.name}/>
              </Radio.Button>,
            )}
          </Radio.Group>
          <input
            type="checkbox"
            value={effectEnabled ? 1 : 0}
            className="setting-input no-txt"
            checked={effectEnabled}
            disabled/>
          <ChatSettingLabel
            className="on-off-wrap"
            onClick={() => {
              cheerForTeamStore.toggleEffectEnabled()
              setEffectIcon(defaultEffectIcon)
            }}>
            <span className="on"></span>
            <span className="off"></span>
          </ChatSettingLabel>
        </CheerForTeamSelectAreaWrap>
      }
      <div className={className ? `${PickEditorClassName} ${className}` : `${PickEditorClassName}`}>
        {visibleBeforeWriteBox && (
          <BeforeWriteWrap className="cursor-pointer"
                           onClick={onClickOpenTextarea}>{beforePlaceholder || '댓글을 남겨보세요'}</BeforeWriteWrap>
        )}
        <TextAreaWrap className={visibleBeforeWriteBox ? 'hide' : 'active'}>
          {isCheerForTeam && editorData.cheerForTeamData && effectEnabled &&
            <SimpleCheerForTeamMatchInfo
              darkBg
              selectedCheerForTeamData={editorData.cheerForTeamData}
              className={'detail-bet-editor'}
            />
          }
          <TextArea
            className={pickCommentTextareaClassName}
            placeholder={placeholder}
            autoSize={{ minRows: minRowsValue, maxRows: maxRowsValue }}
            value={content}
            ref={contentRef}
            minLength={minLength}
            maxLength={maxLength}
            onChange={e => onTextAreaChange(e.target.value)}
          />
          <div className="option-area">
            {/* ************** 내가 업로드하는 파일 영역  ************** */}
            {previewWrap}

            <div className={maxLengthClass}>{getTextLength(content)}/{maxLength}</div>
          </div>

          <div className="line"/>

          <div className="textarea-footer">
            <div className="upload-wrap">
              {showBettingHistory &&
                <Button TP className="upload-btn"
                        onClick={onClickBettingListShareButton}><BettingHistoryIco/></Button>}
              <div className="upload-btn upload-img">
                <ImageUploadIco/>
                <input
                  type="file"
                  accept="image/jpg,image/jpeg,image/png,image/gif,image/bmp,image/tif,image/tiff"
                  name="profile_img"
                  className="upload-img--input"
                  ref={imageRef}
                  onTouchStart={onTouchStartImageAttachHandler}
                  onChange={handleFileOnChange}
                />
              </div>
              <Button TP className="upload-btn" onClick={onClickAttachEmoticon}><EmoticonIco/></Button>
            </div>
            <div className="btn-wrap">
              <Button TP type="button" className="cancel-btn" onClick={onClickCloseTextarea}>취소</Button>
              <Button type="button" className={commentBtnClass} onClick={onClickSaveData}>
                {saveCommentBtnTxt}
              </Button>
            </div>
          </div>

          <EmoticonArea className={emoticonAreaClass}>
            {/* 이모티콘 높이는 키패드 높이와 동일해야합니다.(현재 더미 높이) */}
            <ul className="emoticon-list" css={{ height: '195px' }}>
              {emoticonList.map((emoticon, index) =>
                <li key={`emoticon-area-list-item_${emoticon.pvImg + index}`} className="emoticon-list--item">
                  <Button onClick={() => onClickEmoticonHandler(emoticon.img)} className="emoticon-btn">
                    <img src={emoticonUrlPrefix + emoticon.pvImg} alt={`이모티콘 ${index}`}/>
                  </Button>
                </li>,
              )}
            </ul>
          </EmoticonArea>
        </TextAreaWrap>
      </div>

    </Fragment>
  )
})
